import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Pencil, Clock, FileText, Bell, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_LABELS = {
  baixa: { label: "Baixa", color: "#737373", bg: "#F3F4F6" },
  normal: { label: "Normal", color: "#3B82F6", bg: "#DBEAFE" },
  alta: { label: "Alta", color: "#DC2626", bg: "#FEE2E2" },
};

export default function ClientNotes({ clientId, clientName, embedded = false }) {
  const { apiCall } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    type: "nota",
    title: "",
    content: "",
    reminder_date: "",
    reminder_time: "",
    priority: "normal",
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiCall("get", `/clients/${clientId}/notes`);
      setNotes(res.data);
    } catch {
      toast.error("Erro ao carregar anotações");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) load();
  }, [clientId]);

  const handleOpenNew = (type = "nota") => {
    setEditing(null);
    setForm({
      type,
      title: "",
      content: "",
      reminder_date: type === "lembrete" ? new Date().toISOString().slice(0, 10) : "",
      reminder_time: type === "lembrete" ? "09:00" : "",
      priority: "normal",
    });
    setOpen(true);
  };

  const handleEdit = (note) => {
    setEditing(note);
    const reminderDate = note.reminder_date ? new Date(note.reminder_date) : null;
    setForm({
      type: note.type,
      title: note.title || "",
      content: note.content,
      reminder_date: reminderDate ? reminderDate.toISOString().slice(0, 10) : "",
      reminder_time: reminderDate ? reminderDate.toTimeString().slice(0, 5) : "",
      priority: note.priority || "normal",
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let reminderISO = null;
      if (form.type === "lembrete" && form.reminder_date) {
        reminderISO = new Date(`${form.reminder_date}T${form.reminder_time || "09:00"}`).toISOString();
      }
      const payload = {
        type: form.type,
        title: form.title || null,
        content: form.content,
        reminder_date: reminderISO,
        priority: form.priority,
      };
      if (editing) {
        await apiCall("put", `/notes/${editing.note_id}`, payload);
        toast.success("Anotação atualizada");
      } else {
        await apiCall("post", `/clients/${clientId}/notes`, payload);
        toast.success(form.type === "lembrete" ? "Lembrete criado" : "Anotação criada");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleToggleDone = async (note) => {
    try {
      await apiCall("put", `/notes/${note.note_id}`, { is_done: !note.is_done });
      load();
    } catch {
      toast.error("Erro");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Excluir esta anotação?")) return;
    try {
      await apiCall("delete", `/notes/${id}`);
      toast.success("Excluída");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const formatDateTime = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  };

  const isOverdue = (note) => {
    if (note.type !== "lembrete" || note.is_done || !note.note_id) return false;
    return new Date(note.reminder_date) < new Date();
  };

  return (
    <div className={embedded ? "" : "space-y-4"}>
      {!embedded && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-heading">
              Anotações & Lembretes
            </div>
            <div className="text-lg font-medium font-heading">{clientName}</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => handleOpenNew("nota")} data-testid="add-note-button">
              <FileText className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
              Nota
            </Button>
            <Button size="sm" onClick={() => handleOpenNew("lembrete")} data-testid="add-reminder-button">
              <Bell className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
              Lembrete
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground text-center py-6">Carregando...</p>
      ) : notes.length === 0 ? (
        <Card className="border border-dashed border-border bg-card p-6 shadow-none rounded-md text-center">
          <FileText className="h-6 w-6 mx-auto text-muted-foreground mb-2" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground">Nenhuma anotação ainda. Adicione fórmulas, preferências ou lembretes.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => {
            const overdue = isOverdue(n);
            return (
              <Card
                key={n.note_id}
                className="border border-border bg-card p-3 shadow-soft rounded-md"
                style={{ borderLeftWidth: 3, borderLeftColor: overdue ? "#DC2626" : PRIORITY_LABELS[n.priority]?.color || "#3B82F6" }}
                data-testid={`note-${n.note_id}`}
              >
                <div className="flex items-start gap-3">
                  {n.type === "lembrete" && (
                    <Checkbox
                      checked={n.is_done}
                      onCheckedChange={() => handleToggleDone(n)}
                      className="mt-1"
                      data-testid={`note-done-${n.note_id}`}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span
                        className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                        style={{
                          background: n.type === "lembrete" ? "#FEF3C7" : "#F3F4F6",
                          color: n.type === "lembrete" ? "#92400E" : "#374151",
                        }}
                      >
                        {n.type === "lembrete" ? <Bell className="h-2.5 w-2.5" strokeWidth={2} /> : <FileText className="h-2.5 w-2.5" strokeWidth={2} />}
                        {n.type === "lembrete" ? "Lembrete" : "Nota"}
                      </span>
                      {n.priority !== "normal" && (
                        <span
                          className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded"
                          style={{ background: PRIORITY_LABELS[n.priority]?.bg, color: PRIORITY_LABELS[n.priority]?.color }}
                        >
                          {PRIORITY_LABELS[n.priority]?.label}
                        </span>
                      )}
                      {overdue && (
                        <span className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded inline-flex items-center gap-1" style={{ background: "#FEE2E2", color: "#991B1B" }}>
                          <AlertCircle className="h-2.5 w-2.5" strokeWidth={2} />
                          Atrasado
                        </span>
                      )}
                      {n.is_done && (
                        <span className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded" style={{ background: "#D1FAE5", color: "#166534" }}>
                          ✓ Feito
                        </span>
                      )}
                    </div>
                    {n.title && <div className="font-medium text-sm mb-1">{n.title}</div>}
                    <p className={`text-sm whitespace-pre-wrap ${n.is_done ? "line-through text-muted-foreground" : ""}`}>{n.content}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                      {n.reminder_date && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" strokeWidth={1.5} />
                          {formatDateTime(n.reminder_date)}
                        </span>
                      )}
                      <span>Por {n.created_by_name}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(n)} data-testid={`note-edit-${n.note_id}`}>
                      <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(n.note_id)} data-testid={`note-delete-${n.note_id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {embedded && (
        <div className="flex gap-2 mt-3">
          <Button size="sm" variant="outline" onClick={() => handleOpenNew("nota")} data-testid="add-note-button-embedded">
            <FileText className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
            Nova Nota
          </Button>
          <Button size="sm" onClick={() => handleOpenNew("lembrete")} data-testid="add-reminder-button-embedded">
            <Bell className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
            Novo Lembrete
          </Button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">
              {editing ? "Editar" : "Nova"} {form.type === "lembrete" ? "Lembrete" : "Anotação"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="note-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nota" data-testid="note-type-nota">📄 Nota (observação sobre o cliente)</SelectItem>
                  <SelectItem value="lembrete" data-testid="note-type-lembrete">🔔 Lembrete (com data/hora)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Título (opcional)</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Fórmula coloração" data-testid="note-title-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Conteúdo</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder={form.type === "lembrete" ? "Ex: Ligar para confirmar retorno" : "Ex: Alérgica a amônia. Fórmula 7.0 + 8.4"}
                required
                rows={3}
                data-testid="note-content-input"
              />
            </div>
            {form.type === "lembrete" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Data</Label>
                  <Input type="date" value={form.reminder_date} onChange={(e) => setForm({ ...form, reminder_date: e.target.value })} required data-testid="note-date-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Hora</Label>
                  <Input type="time" value={form.reminder_time} onChange={(e) => setForm({ ...form, reminder_time: e.target.value })} required data-testid="note-time-input" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest">Prioridade</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="note-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k} data-testid={`note-priority-${k}`}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="note-save-button">{editing ? "Atualizar" : "Criar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
